import * as React from 'react';
import { useDeleteTransportRoute } from 'hooks/api/tranpsort-routes';
import { useTranslation } from 'hooks';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/ButtonGs';

import { translations } from './translations';
import { notify } from 'services/notification';
import { useHistory } from 'react-router-dom';
import { TRANSPORT_ROUTES } from 'routes/transport-routes';
type Ref = {
  openDialog: () => void;
  closeDialog: () => void;
};

export type DeleteTransportRouteDialogRef = Ref;

type Props = {
  id: number | undefined;
  name?: string;
};

export const DeleteTransportRouteDialog = React.forwardRef<Ref, Props>(
  ({ id, name }, ref) => {
    const { getTranslation } = useTranslation(translations);
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const { deleteTransportRoute } = useDeleteTransportRoute();
    const navigate = useHistory().push;
    const openDialog = React.useCallback(() => {
      setOpen(true);
    }, []);

    const closeDialog = React.useCallback(() => {
      setOpen(false);
    }, []);

    const navigateToListRoutesPage = React.useCallback(() => {
      navigate(TRANSPORT_ROUTES.listRoutes().path);
    }, [navigate]);

    const handleSubmit = React.useCallback(async () => {
      if (!id) return;

      setLoading(true);
      try {
        await deleteTransportRoute(id);
        closeDialog();
        notify('success', getTranslation('routeDeleteSuccessMessage'));
        navigateToListRoutesPage();
      } catch (error) {
        notify('error', getTranslation('routeDeleteErrorMessage'));
      } finally {
        setLoading(false);
      }
    }, [id, deleteTransportRoute, closeDialog]);

    React.useImperativeHandle(ref, () => {
      return {
        openDialog,
        closeDialog,
      };
    }, [openDialog, closeDialog]);

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {getTranslation('dialogTitle')}: {name}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {getTranslation('dialogDescription')}
          </DialogDescription>

          <div className={'flex justify-end gap-4'}>
            <Button.Root
              size={'sm'}
              variant={'secondary'}
              type={'button'}
              onClick={closeDialog}
              disabled={loading}
            >
              {getTranslation('cancelButton')}
            </Button.Root>
            <Button.Root
              size={'sm'}
              variant={'danger'}
              type={'button'}
              onClick={handleSubmit}
              disabled={loading || !id}
            >
              {getTranslation('deleteButton')}
            </Button.Root>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);
DeleteTransportRouteDialog.displayName = 'DeleteTransportRouteDialog';
